/**
 * PEStart KI-Text-Engine — Context aus Live-Calc, Privatkostenkompass + Gründungs-Extras.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import usePEStartStore, {
    getInvestmentSums,
    normalizeGeneratedSections,
    type PEStartGeneratedSection,
    type PEStartState,
} from '../../store/usePEStartStore';
import {
    collectStaffFromCalc,
    computePEStartDashboard,
    resolveUnternehmerBedarf,
} from './peStartCalcAdapter';
import {
    computePEStartAufbauPlan,
    type PEStartAufbauPlanModel,
} from './peStartPlanungsBridge';
import { downloadPEStartBusinessPlanPdf } from './peStartBusinessPlanPdf';

export type BusinessPlanSectionId =
    | 'executive_summary'
    | 'finanzierung_investition'
    | 'personal_preis'
    | 'liquiditaet_prognose'
    | 'aufbau_verbesserung';

export type PrivatkostenKategorieId =
    | 'housing'
    | 'lifestyle'
    | 'social'
    | 'mobility'
    | 'periodisch'
    | 'sonstig';

export type InvestitionsZeitraumKlassifikation =
    | 'website_leasing'
    | 'schulung_seminar'
    | 'sonstig';

export type BwaMonatZeile = {
    month: number;
    umsatzerloese: number;
    materialaufwand: number;
    wareneinsatzProzent: number;
    rohertrag: number;
    personalkosten: number;
    raumUndFixkosten: number;
    operativesErgebnisEbitda: number;
    kapitaldienst: number;
    zinsen: number;
    vorsteuerErgebnis: number;
    liquiditaetssaldo: number;
};

export type BusinessPlanContext = {
    meta: {
        module: 'PEStart';
        version: 4;
        generatedAt: string;
        foundingType: PEStartState['foundingType'];
        foundingTypeLabel: string;
        currencyCode: string;
        language: 'de';
        dataSources: string[];
        /** Alle Geldbeträge im Context sind kaufmännisch auf volle Euro gerundet */
        moneyRounding: 'volle_euro';
    };
    /** Banken-Briefkopf aus Stammdaten */
    stammdaten: {
        inhaberName: string;
        firmenname: string;
        strasse: string;
        plz: string;
        ort: string;
        adresseZeile: string;
        foundingTypeLabel: string;
        datum: string;
    };
    managementSummary: {
        foundingType: PEStartState['foundingType'];
        unternehmerlohnKompass: number;
        unternehmerlohnKompassRounded: number;
        unternehmerlohnSource: 'kompass' | 'pauschale';
        stundensatzErforderlich: number;
        totpunktMonatNetto: number;
        totpunktMonatBrutto: number;
        planumsatzNetto: number;
        startkapitalBedarf: number;
        auslastungsziel: number;
        breakEvenMonat: number | null;
    };
    /** Freitext-Leitfaden für ausführliche Executive Summary (PEStart-Store) */
    executiveSummaryNotes: string;
    fachlicheQualifikation: string;
    berufserfahrung: string;
    kaufmaennischeEignung: string;
    /** Nur PEStart — nicht Privatkostenkompass */
    privateAltersvorsorge: number;
    liquiditaetspufferMonat: number;
    /** Privatkostenkompass — Banken-Raster Unternehmerlohn */
    privatkostenAufschluesselung: {
        source: 'kompass' | 'pauschale';
        kategorien: Array<{
            id: PrivatkostenKategorieId;
            label: string;
            betragMonat: number;
            items: Array<{ name: string; betragMonat: number }>;
        }>;
        summeMonat: number;
        summeGerundet: number;
        hinweis: string;
    };
    finanzierungUndKapitalbedarf: {
        investitionenGesamt: number;
        /** Explizite Bank-Aufschlüsselung (volle Euro) */
        investitionenGerundet: number;
        anlaufPufferGerundet: number;
        gesamtkapitalbedarfGerundet: number;
        kreditbetragGerundet: number;
        foerderungenGesamtGerundet: number;
        investitionenDetail: {
            erstausstattung: number;
            umbau: number;
            marketing: number;
            kaufpreis: number;
            uebernahmekosten: number;
            sanierungInventar: number;
            items: PEStartState['investments']['items'];
            newTotal: number;
            takeoverTotal: number;
        };
        foerderungenGesamt: number;
        foerderungen: PEStartState['subsidies'];
        foerderungenProMonat: { month: number; amount: number }[];
        startFinanzbedarf: number;
        kreditplan: {
            kreditbetrag: number;
            laufzeitJahre: number;
            zinssatzProzent: number;
            rateMonat: number;
            zinsAnteilMonat1: number;
            tilgungAnteilMonat1: number;
            gesamtzahlung: number;
            gesamtzins: number;
        };
    };
    /** BWA-Ertragslage M1–M6 (volle Euro) */
    bwaErtragslage: {
        basis: {
            planumsatzNetto: number;
            materialkostenMonat: number;
            baseMaterialProzent: number;
            personalkostenBrutto: number;
            raumkostenMonat: number;
            betriebskostenMonat: number;
            sonstigeFixkostenMonat: number;
            kapitaldienstMonat: number;
            zinsenMonat1: number;
        };
        monate: BwaMonatZeile[];
    };
    personalUndAuslastung: {
        /** Anonymisiert — keine Vornamen, nur Rollen */
        staff: Array<{
            id: string;
            rolle: string;
            totalCost: number;
            fromTakeover: boolean;
            startMonth: number;
            pool: string;
        }>;
        /** z. B. "4 produktive Fachkräfte, 1 administrative Kraft, 1 Auszubildender" */
        personalRollenZusammenfassung: string;
        zielStundensatz: number;
        auslastungsziel: number;
        monatsstundenGesamt: number;
        anlaufAuslastungProMonat: { month: number; anlaufAuslastung: number }[];
    };
    liquiditaetUndRentabilitaet: {
        breakEvenMonat: number | null;
        monate: Array<{
            month: number;
            umsatzZiel: number;
            umsatzPrognose: number;
            anlaufAuslastung: number;
            verbesserungAnteilBrutto: number;
            fixkosten: number;
            personalkosten: number;
            unternehmerlohn: number;
            foerderung: number;
            investition: number;
            kreditAuszahlung: number;
            kreditrate: number;
            liquiditaetssaldo: number;
            decktTotpunkt: boolean;
        }>;
    };
    /** Regeln für Website-Leasing vs. Schulung/Seminar */
    investitionsZeitraeume: {
        regeln: Array<{
            klassifikation: InvestitionsZeitraumKlassifikation;
            titel: string;
            beschreibung: string;
        }>;
        positionen: Array<{
            label: string;
            category: string;
            amount: number;
            klassifikation: InvestitionsZeitraumKlassifikation;
            beschreibungRegel: string;
        }>;
    };
    aufbauUndVerbesserung: {
        available: boolean;
        sourceLabel: string;
        szenarioName: string;
        basisDatenTyp: string | null;
        hebel: PEStartAufbauPlanModel['hebel'];
        summary: PEStartAufbauPlanModel['summary'];
    };
    promptSections: {
        id: BusinessPlanSectionId;
        title: string;
        instruction: string;
        dataKeys: string[];
    }[];
};

const FOUNDING_LABEL: Record<PEStartState['foundingType'], string> = {
    NEW: 'Neugründung',
    TAKEOVER: 'Übernahme',
};

/** Kaufmännische Rundung auf volle Euro (keine Cent). */
export function roundEuro(value: number): number {
    return Math.round(Number(value) || 0);
}

const POOL_ROLLE_SINGULAR: Record<string, string> = {
    produktiv: 'produktive Fachkraft',
    unproduktiv: 'administrative Kraft',
    azubi: 'Auszubildender',
};

const POOL_ROLLE_PLURAL: Record<string, string> = {
    produktiv: 'produktive Fachkräfte',
    unproduktiv: 'administrative Kräfte',
    azubi: 'Auszubildende',
};

/** Kurze Rollen für Hebel-Anonymisierung (ohne Vornamen) */
const HEBEL_ROLLE: Record<string, string> = {
    produktiv: 'Fachkraft',
    unproduktiv: 'administrative Kraft',
    azubi: 'Auszubildender',
};

const PRIVATKOSTEN_LABELS: Record<string, string> = {
    housing: 'Wohnen / Miete',
    lifestyle: 'Lebenshaltung',
    social: 'Vorsorge / Versicherungen (Sozialabgaben)',
    mobility: 'Mobilität / private Kredite & Leasing',
};

function buildPrivatkostenAufschluesselung(
    privateCostData: any,
    ulSource: 'kompass' | 'pauschale',
    ulBedarf: number,
    ulRounded: number
): BusinessPlanContext['privatkostenAufschluesselung'] {
    const cats = Array.isArray(privateCostData?.monthlyCats)
        ? privateCostData.monthlyCats
        : [];
    const kategorien: BusinessPlanContext['privatkostenAufschluesselung']['kategorien'] =
        [];

    for (const cat of cats) {
        const id = String(cat?.id || 'sonstig') as PrivatkostenKategorieId;
        const items = (cat?.items || [])
            .map((it: any) => ({
                name: String(it?.name || '').trim() || 'Position',
                betragMonat: roundEuro(it?.value),
            }))
            .filter((it: { betragMonat: number }) => it.betragMonat !== 0);
        const betragMonat = roundEuro(
            (cat?.items || []).reduce(
                (s: number, it: any) => s + (Number(it?.value) || 0),
                0
            )
        );
        kategorien.push({
            id: (['housing', 'lifestyle', 'social', 'mobility'] as string[]).includes(
                id
            )
                ? id
                : 'sonstig',
            label:
                PRIVATKOSTEN_LABELS[id] ||
                String(cat?.title || id).replace(/^[^\wÄÖÜäöüß]+/, '').trim() ||
                'Sonstiges',
            betragMonat,
            items,
        });
    }

    const periodItems = (privateCostData?.periodicItems || [])
        .map((it: any) => {
            const freq = Math.max(1, Number(it?.frequency) || 12);
            return {
                name: String(it?.name || '').trim() || 'Periodischer Posten',
                betragMonat: roundEuro((Number(it?.value) || 0) / freq),
            };
        })
        .filter((it: { betragMonat: number }) => it.betragMonat !== 0);
    const periodSum = roundEuro(
        periodItems.reduce((s: number, it: { betragMonat: number }) => s + it.betragMonat, 0)
    );
    if (periodItems.length > 0 || periodSum > 0) {
        kategorien.push({
            id: 'periodisch',
            label: 'Periodische Posten (monatlicher Anteil)',
            betragMonat: periodSum,
            items: periodItems,
        });
    }

    const summeMonat = roundEuro(
        kategorien.reduce((s, k) => s + k.betragMonat, 0) || ulBedarf
    );

    return {
        source: ulSource,
        kategorien,
        summeMonat: ulSource === 'kompass' ? summeMonat : roundEuro(ulBedarf),
        summeGerundet: roundEuro(ulRounded),
        hinweis:
            ulSource === 'kompass'
                ? 'Unternehmerlohn = Summe Privatkostenkompass (volle Euro).'
                : 'Kein gefüllter Kompass — Pauschale aus Unternehmerdaten / Calc.',
    };
}

function buildBwaErtragslage(
    calc: any,
    clientData: any,
    monateGerundet: BusinessPlanContext['liquiditaetUndRentabilitaet']['monate'],
    kreditplan: BusinessPlanContext['finanzierungUndKapitalbedarf']['kreditplan'],
    planumsatzNetto: number
): BusinessPlanContext['bwaErtragslage'] {
    const mwst = Number(calc?.mwstSatz) || 0;
    const mwstFaktor = 1 + mwst / 100;
    const zeitraum =
        Number(calc?.zeitraum) ||
        Number(clientData?.bwaData?.zeitraum) ||
        Number(calc?.clientData?.bwaData?.zeitraum) ||
        12;
    const bwa = clientData?.bwaData || calc?.clientData?.bwaData || {};
    const raumkostenMonat = roundEuro((Number(bwa.raumkosten) || 0) / Math.max(1, zeitraum));
    const betriebskostenMonat = roundEuro(calc?.betriebskostenMonat);
    const sonstigeFixkostenMonat = Math.max(0, betriebskostenMonat - raumkostenMonat);
    const raumUndFixkosten = raumkostenMonat + sonstigeFixkostenMonat;
    const baseMaterialProzent = Number(calc?.baseMaterialProzent) || 0;
    const materialkostenMonat = roundEuro(calc?.materialkostenMonat);
    const personalkostenBrutto = roundEuro(
        (Number(calc?.produktiveLohnkostenMitarbeiter) || 0) +
            (Number(calc?.unproduktiveLohnkostenMitarbeiter) || 0) +
            (Number(calc?.azubiKostenGesamt) || 0)
    );

    const monate: BwaMonatZeile[] = monateGerundet.map((m) => {
        const umsatzerloese = roundEuro(
            mwstFaktor > 0 ? m.umsatzPrognose / mwstFaktor : m.umsatzPrognose
        );
        const materialaufwand = roundEuro(
            baseMaterialProzent > 0
                ? (umsatzerloese * baseMaterialProzent) / 100
                : planumsatzNetto > 0
                  ? (umsatzerloese / planumsatzNetto) * materialkostenMonat
                  : materialkostenMonat
        );
        const rohertrag = roundEuro(umsatzerloese - materialaufwand);
        const personalkosten = roundEuro(m.personalkosten);
        const operativesErgebnisEbitda = roundEuro(
            rohertrag - personalkosten - raumUndFixkosten
        );
        const kapitaldienst = roundEuro(m.kreditrate);
        const zinsen =
            m.month === 1
                ? roundEuro(kreditplan.zinsAnteilMonat1)
                : roundEuro(
                      kapitaldienst > 0
                          ? Math.min(kapitaldienst, kreditplan.zinsAnteilMonat1)
                          : 0
                  );
        const vorsteuerErgebnis = roundEuro(operativesErgebnisEbitda - kapitaldienst);
        return {
            month: m.month,
            umsatzerloese,
            materialaufwand,
            wareneinsatzProzent: baseMaterialProzent,
            rohertrag,
            personalkosten,
            raumUndFixkosten: roundEuro(raumUndFixkosten),
            operativesErgebnisEbitda,
            kapitaldienst,
            zinsen,
            vorsteuerErgebnis,
            liquiditaetssaldo: roundEuro(m.liquiditaetssaldo),
        };
    });

    return {
        basis: {
            planumsatzNetto: roundEuro(planumsatzNetto),
            materialkostenMonat,
            baseMaterialProzent,
            personalkostenBrutto,
            raumkostenMonat,
            betriebskostenMonat,
            sonstigeFixkostenMonat: roundEuro(sonstigeFixkostenMonat),
            kapitaldienstMonat: roundEuro(kreditplan.rateMonat),
            zinsenMonat1: roundEuro(kreditplan.zinsAnteilMonat1),
        },
        monate,
    };
}

const INVEST_ZEITRAUM_REGELN: BusinessPlanContext['investitionsZeitraeume']['regeln'] = [
    {
        klassifikation: 'website_leasing',
        titel: 'Website-Leasing',
        beschreibung:
            'Fortlaufende, monatliche Betriebsausgabe (Mindestlaufzeit 24 Monate / open end) — kein einmaliger Investitionsblock.',
    },
    {
        klassifikation: 'schulung_seminar',
        titel: 'Schulungen / Seminare',
        beschreibung:
            'Einmalige Bildungs-/Qualifikationsinvestition; Kosten kalkulatorisch zur Amortisation auf 12 Monate verteilt.',
    },
    {
        klassifikation: 'sonstig',
        titel: 'Sonstige Positionen',
        beschreibung: 'Nach individueller Laufzeit / Zahlungsweise im Context beschreiben.',
    },
];

function classifyInvestmentZeitraum(
    label: string,
    category: string
): InvestitionsZeitraumKlassifikation {
    const hay = `${label} ${category}`.toLowerCase();
    if (
        /website|webseite|homepage|web[- ]?leasing|domain[- ]?leasing|hosting[- ]?leasing/.test(
            hay
        ) ||
        (/leasing/.test(hay) && /web|site|seite|homepage/.test(hay))
    ) {
        return 'website_leasing';
    }
    if (
        /seminar|schulung|simplie|workshop|fortbildung|qualifikation|weiterbildung|coaching/.test(
            hay
        )
    ) {
        return 'schulung_seminar';
    }
    return 'sonstig';
}

function buildPersonalRollenZusammenfassung(
    staff: Array<{ pool: string }>
): string {
    const counts: Record<string, number> = {
        produktiv: 0,
        unproduktiv: 0,
        azubi: 0,
    };
    for (const s of staff) {
        const p = s.pool in counts ? s.pool : 'produktiv';
        counts[p] += 1;
    }
    const parts: string[] = [];
    (['produktiv', 'unproduktiv', 'azubi'] as const).forEach((pool) => {
        const n = counts[pool];
        if (n <= 0) return;
        parts.push(
            `${n} ${n === 1 ? POOL_ROLLE_SINGULAR[pool] : POOL_ROLLE_PLURAL[pool]}`
        );
    });
    return parts.length > 0 ? parts.join(', ') : 'kein zusätzliches Personal erfasst';
}

function buildInvestitionsZeitraeume(
    investments: PEStartState['investments']
): BusinessPlanContext['investitionsZeitraeume'] {
    const positionen = (investments?.items || []).map((it) => {
        const klassifikation = classifyInvestmentZeitraum(
            String(it.label || ''),
            String(it.category || '')
        );
        const regel = INVEST_ZEITRAUM_REGELN.find((r) => r.klassifikation === klassifikation);
        return {
            label: String(it.label || 'Position'),
            category: String(it.category || ''),
            amount: roundEuro(it.amount),
            klassifikation,
            beschreibungRegel: regel?.beschreibung || INVEST_ZEITRAUM_REGELN[2].beschreibung,
        };
    });
    return { regeln: INVEST_ZEITRAUM_REGELN, positionen };
}

function anonymizeHebelText(
    text: string,
    nameToRolle: Array<{ name: string; rolle: string }>
): string {
    let out = String(text || '');
    const sorted = [...nameToRolle].sort((a, b) => b.name.length - a.name.length);
    for (const { name, rolle } of sorted) {
        if (!name || name.length < 2) continue;
        const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        out = out.replace(new RegExp(esc, 'gi'), rolle);
        const first = name.split(/\s+/)[0];
        if (first && first.length >= 3 && first.toLowerCase() !== name.toLowerCase()) {
            const escFirst = first.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            out = out.replace(new RegExp(`\\b${escFirst}\\b`, 'gi'), rolle);
        }
    }
    return out;
}

function anonymizeAufbauHebel(
    hebel: PEStartAufbauPlanModel['hebel'],
    staffRaw: Array<{ name: string; pool: string }>
): PEStartAufbauPlanModel['hebel'] {
    const nameToRolle = staffRaw
        .map((m) => ({
            name: String(m.name || '').trim(),
            rolle: HEBEL_ROLLE[m.pool] || 'Fachkraft',
        }))
        .filter((m) => m.name.length > 0);

    return (hebel || []).map((h) => {
        let titel = anonymizeHebelText(h.titel, nameToRolle);
        const detail = anonymizeHebelText(h.detail, nameToRolle);
        const wirkungHinweis = h.wirkungHinweis
            ? anonymizeHebelText(h.wirkungHinweis, nameToRolle)
            : undefined;

        if (h.kategorie === 'PERSONAL') {
            const rawMatch = String(h.titel || '').match(
                /Personal:\s*([^(\n]+?)(?:\s*\(|$)/i
            );
            const rawName = (rawMatch?.[1] || '').trim();
            const found = nameToRolle.find(
                (n) =>
                    n.name.toLowerCase() === rawName.toLowerCase() ||
                    (rawName.length >= 3 &&
                        n.name.toLowerCase().includes(rawName.toLowerCase())) ||
                    (rawName.length >= 3 &&
                        rawName.toLowerCase().includes(n.name.split(/\s+/)[0].toLowerCase()))
            );
            const rolle = found?.rolle || 'Fachkraft';
            const entlassung = /\(Entlassung\)/i.test(h.titel) ? ' (Entlassung)' : '';
            titel = `Personal: ${rolle}${entlassung}`;
        }

        return {
            ...h,
            titel,
            detail,
            wirkungHinweis,
        };
    });
}

function buildStammdaten(
    clientData: any,
    foundingTypeLabel: string,
    generatedAt: string
): BusinessPlanContext['stammdaten'] {
    const vorname = String(clientData?.vorname || '').trim();
    const nachname = String(clientData?.nachname || '').trim();
    const inhaberName =
        [vorname, nachname].filter(Boolean).join(' ') ||
        String(clientData?.unternehmername || clientData?.name || '').trim() ||
        '—';
    const firmenname =
        String(
            clientData?.firmenname ||
                clientData?.salonname ||
                clientData?.unternehmen ||
                ''
        ).trim() || '—';
    const strasse = String(clientData?.strasse || '').trim();
    const plz = String(clientData?.plz || '').trim();
    const ort = String(clientData?.ort || '').trim();
    const adresseZeile = [strasse, [plz, ort].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
    let datum = '—';
    try {
        datum = new Date(generatedAt).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    } catch {
        datum = new Date().toLocaleDateString('de-DE');
    }
    return {
        inhaberName,
        firmenname,
        strasse,
        plz,
        ort,
        adresseZeile: adresseZeile || '—',
        foundingTypeLabel,
        datum,
    };
}

const PROMPT_SECTIONS: BusinessPlanContext['promptSections'] = [
    {
        id: 'executive_summary',
        title: '1. Executive Summary',
        instruction:
            'Management Summary mit Gründungstyp, Totpunkt, Stundensatz und Startkapital. Nutze executiveSummaryNotes als Leitfaden für die Vorhabensbeschreibung. PFLICHT: fachlicheQualifikation, berufserfahrung, kaufmaennischeEignung vollständig einarbeiten. PFLICHT: privateAltersvorsorge und liquiditaetspufferMonat als zusätzliche private Absicherung (PEStart, nicht Kompass) nennen. PFLICHT: Unternehmerlohn aus privatkostenAufschluesselung (Wohnen, Lebenshaltung, Vorsorge/Versicherungen, Mobilität, periodische Posten). Nur volle Euro. Keine Mitarbeitervornamen.',
        dataKeys: [
            'managementSummary',
            'stammdaten',
            'executiveSummaryNotes',
            'fachlicheQualifikation',
            'berufserfahrung',
            'kaufmaennischeEignung',
            'privateAltersvorsorge',
            'liquiditaetspufferMonat',
            'privatkostenAufschluesselung',
        ],
    },
    {
        id: 'finanzierung_investition',
        title: '2. Finanzierungs- & Investitionsplan',
        instruction:
            'Kapitalbedarfsplan strikt nach finanzierungUndKapitalbedarf: investitionenGerundet (Investitionen / Kaufpreis·Sanierung·Umbau), anlaufPufferGerundet als Betriebsmittel- & Anlaufpuffer (= gesamtkapitalbedarfGerundet − investitionenGerundet), gesamtkapitalbedarfGerundet sowie kreditbetragGerundet. Beschreibe den Betriebsmittelbedarf exakt als diese Differenz. Kredit deckt den gerundeten Gesamtkapitalbedarf (nach Abzug Förderungen laut Context). Nur volle Euro, keine Cent, keine erfundenen Zwischenbeträge.',
        dataKeys: ['finanzierungUndKapitalbedarf'],
    },
    {
        id: 'personal_preis',
        title: '3. Personal- & Preisstruktur',
        instruction:
            'Personal ausschließlich über personalRollenZusammenfassung und anonymisierte staff[].rolle (keine Vornamen, keine Klarnamen!). Beschreibe Rollen und Anzahlen, Startmonate/Übernahme-Flags ohne Namen; Ziel-Stundensatz und Anlauf-Auslastung. STRIKTES VERBOT: Mitarbeitervornamen oder personenbezogene Namen.',
        dataKeys: ['personalUndAuslastung'],
    },
    {
        id: 'liquiditaet_prognose',
        title: '4. Liquiditäts-Prognose',
        instruction:
            'PFLICHT: Lege die BWA-Ertragslage aus bwaErtragslage für M1–M6 chronologisch dar — je Monat: Umsatzerlöse (Plan-/Prognoseumsatz) − Materialaufwand/Wareneinsatz → Rohertrag − Personalkosten − Raum- & sonstige Fixkosten → Operatives Ergebnis (EBITDA) − Kapitaldienst/Zinsen (kreditplan) → Vorsteuer-Ergebnis und Liquiditätssaldo. Zusätzlich Break-Even nennen. Nur volle Euro aus dem Context, keine Cent.',
        dataKeys: ['bwaErtragslage', 'liquiditaetUndRentabilitaet', 'finanzierungUndKapitalbedarf'],
    },
    {
        id: 'aufbau_verbesserung',
        title: '5. Aufbau- & Verbesserungsplan',
        instruction:
            'Schreibe Kapitel 5 in vollständigen, grammatikalisch korrekten Sätzen bis zum Kapitelende — keine Satzfragmente und keine abgebrochenen Personal-Sätze (VERBOTEN z. B. „…und die geplante Entlassung ein Kostenreduktion.“). Formuliere Personal-Hebel klar: Rolle + Maßnahme + Wirkung in einem ganzen Satz. Ist-/Soll-Vergleiche ausschließlich im Format „IST 1.838 EUR -> ZIEL 1.688 EUR“ (kein Ausrufezeichen, kein „!“ zwischen Beträgen). Hebel nur mit Rollen, nie Vornamen. investitionsZeitraeume: Website-Leasing = fortlaufende monatliche Betriebsausgabe (Mindestlaufzeit 24 Monate / open end); Schulungen/Seminare (z. B. Simplie) = einmalige Qualifikationsinvestition mit kalkulatorischer Amortisation über 12 Monate. Wenn available=false, auf fehlende Planungsanalyse hinweisen.',
        dataKeys: [
            'aufbauUndVerbesserung',
            'investitionsZeitraeume',
            'personalUndAuslastung',
        ],
    },
];

export function buildBusinessPlanContext(
    state: PEStartState,
    calc: any,
    privateCostData: any,
    aufbau?: PEStartAufbauPlanModel | null,
    clientData?: any
): BusinessPlanContext {
    const verbesserungDelta = aufbau?.available ? aufbau.summary?.umsatzDeltaMonat ?? 0 : 0;
    const model = computePEStartDashboard(state, calc, privateCostData, verbesserungDelta);
    const sums = getInvestmentSums(state.investments);
    const pauschale =
        Number(calc?.gesamtlohn) ||
        (Number(calc?.clientData?.unternehmerData?.entnahme) || 0) +
            (Number(calc?.clientData?.unternehmerData?.sozialkosten) || 0);
    const ul = resolveUnternehmerBedarf(privateCostData, pauschale);
    const staffRaw = collectStaffFromCalc(calc);
    const aufbauAvailable = !!aufbau?.available;
    const generatedAt = new Date().toISOString();
    const foundingTypeLabel = FOUNDING_LABEL[state.foundingType];

    const investitionenGerundet = roundEuro(model.kpis.investitionenGesamt);
    const anlaufPufferGerundet = roundEuro(
        model.kpis.finanzbedarf.anlaufLiquiditaetspuffer
    );
    const gesamtkapitalbedarfGerundet = investitionenGerundet + anlaufPufferGerundet;
    const foerderungenGesamtGerundet = roundEuro(model.kpis.foerderungenGesamt);
    const kreditbetragGerundet = roundEuro(model.kpis.finanzbedarf.kreditbetrag);

    const anonymousStaff = staffRaw.map((m) => {
        const flag = state.staffFlags[m.id] || {
            fromTakeover: state.foundingType === 'TAKEOVER',
            startMonth: 1,
        };
        return {
            id: m.id,
            rolle: POOL_ROLLE_SINGULAR[m.pool] || 'Mitarbeiter',
            totalCost: roundEuro(m.totalCost),
            fromTakeover: !!flag.fromTakeover,
            startMonth: flag.startMonth,
            pool: m.pool,
        };
    });

    const monateGerundet = model.months.map((m) => ({
        month: m.month,
        umsatzZiel: roundEuro(m.umsatzZiel),
        umsatzPrognose: roundEuro(m.umsatzPrognose),
        anlaufAuslastung: Number(m.anlaufAuslastung) || 0,
        verbesserungAnteilBrutto: roundEuro(m.verbesserungAnteilBrutto),
        fixkosten: roundEuro(m.fixkosten),
        personalkosten: roundEuro(m.personalkosten),
        unternehmerlohn: roundEuro(m.unternehmerlohn),
        foerderung: roundEuro(m.foerderung),
        investition: roundEuro(m.investition),
        kreditAuszahlung: roundEuro(m.kreditAuszahlung),
        kreditrate: roundEuro(m.kreditrate),
        liquiditaetssaldo: roundEuro(m.liquiditaetssaldo),
        decktTotpunkt: !!m.decktTotpunkt,
    }));

    const kreditplan = {
        kreditbetrag: kreditbetragGerundet,
        laufzeitJahre: model.kpis.finanzbedarf.tilgungLaufzeitJahre,
        zinssatzProzent: model.kpis.finanzbedarf.zinssatzProzent,
        rateMonat: roundEuro(model.kpis.finanzbedarf.rateMonat),
        zinsAnteilMonat1: roundEuro(model.kpis.finanzbedarf.zinsAnteilMonat1),
        tilgungAnteilMonat1: roundEuro(
            model.kpis.finanzbedarf.tilgungAnteilMonat1
        ),
        gesamtzahlung: roundEuro(model.kpis.finanzbedarf.gesamtzahlung),
        gesamtzins: roundEuro(model.kpis.finanzbedarf.gesamtzins),
    };

    const planumsatzNetto = roundEuro(model.kpis.planumsatzNetto);
    const privatkostenAufschluesselung = buildPrivatkostenAufschluesselung(
        privateCostData,
        ul.source,
        ul.bedarf,
        ul.bedarfRounded
    );
    const bwaErtragslage = buildBwaErtragslage(
        calc,
        clientData || calc?.clientData,
        monateGerundet,
        kreditplan,
        planumsatzNetto
    );
    const investitionsZeitraeume = buildInvestitionsZeitraeume(state.investments);
    const hebelAnonym = aufbauAvailable
        ? anonymizeAufbauHebel(aufbau?.hebel || [], staffRaw)
        : [];

    return {
        meta: {
            module: 'PEStart',
            version: 4,
            generatedAt,
            foundingType: state.foundingType,
            foundingTypeLabel,
            currencyCode: model.currencyCode,
            language: 'de',
            moneyRounding: 'volle_euro',
            dataSources: [
                'useCalculationsSelector.sollumsatz',
                'useCalculationsSelector.planumsatzGesamtNetto',
                'useCalculationsSelector.materialkostenMonat / baseMaterialProzent',
                'useCalculationsSelector.betriebskostenMonat / bwaData.raumkosten',
                'useCalculationsSelector.personalData (anonymisiert)',
                ul.source === 'kompass'
                    ? 'clientData.privateCostData (Aufschlüsselung)'
                    : 'unternehmerData.entnahme+sozialkosten / calc.gesamtlohn (Pauschale)',
                ...(aufbauAvailable
                    ? ['planungsAnalysePrognose (read-only Aufbau-Plan, anonymisiert)']
                    : []),
            ],
        },
        stammdaten: buildStammdaten(
            clientData || calc?.clientData,
            foundingTypeLabel,
            generatedAt
        ),
        managementSummary: {
            foundingType: state.foundingType,
            unternehmerlohnKompass: roundEuro(ul.bedarf),
            unternehmerlohnKompassRounded: roundEuro(ul.bedarfRounded),
            unternehmerlohnSource: ul.source,
            stundensatzErforderlich: roundEuro(model.kpis.stundensatzErforderlich),
            totpunktMonatNetto: roundEuro(model.kpis.totpunktMonatNetto),
            totpunktMonatBrutto: roundEuro(model.kpis.totpunktMonatBrutto),
            planumsatzNetto,
            startkapitalBedarf: kreditbetragGerundet,
            auslastungsziel: Number(model.kpis.auslastungsziel) || 0,
            breakEvenMonat: model.breakEvenMonat,
        },
        executiveSummaryNotes: String(state.executiveSummaryNotes || ''),
        fachlicheQualifikation: String(state.fachlicheQualifikation || ''),
        berufserfahrung: String(state.berufserfahrung || ''),
        kaufmaennischeEignung: String(state.kaufmaennischeEignung || ''),
        privateAltersvorsorge: roundEuro(state.privateAltersvorsorge),
        liquiditaetspufferMonat: roundEuro(state.liquiditaetspufferMonat),
        privatkostenAufschluesselung,
        finanzierungUndKapitalbedarf: {
            investitionenGesamt: investitionenGerundet,
            investitionenGerundet,
            anlaufPufferGerundet,
            gesamtkapitalbedarfGerundet,
            kreditbetragGerundet,
            foerderungenGesamtGerundet,
            investitionenDetail: {
                erstausstattung: roundEuro(state.investments.erstausstattung),
                umbau: roundEuro(state.investments.umbau),
                marketing: roundEuro(state.investments.marketing),
                kaufpreis: roundEuro(state.investments.kaufpreis),
                uebernahmekosten: roundEuro(state.investments.uebernahmekosten),
                sanierungInventar: roundEuro(state.investments.sanierungInventar),
                items: state.investments.items,
                newTotal: roundEuro(sums.newTotal),
                takeoverTotal: roundEuro(sums.takeoverTotal),
            },
            foerderungenGesamt: foerderungenGesamtGerundet,
            foerderungen: (state.subsidies || []).map((s) => ({
                ...s,
                amount: roundEuro(s.amount),
            })),
            foerderungenProMonat: model.months.map((m) => ({
                month: m.month,
                amount: roundEuro(m.foerderung),
            })),
            startFinanzbedarf: kreditbetragGerundet,
            kreditplan,
        },
        bwaErtragslage,
        personalUndAuslastung: {
            staff: anonymousStaff,
            personalRollenZusammenfassung:
                buildPersonalRollenZusammenfassung(anonymousStaff),
            zielStundensatz: roundEuro(model.kpis.stundensatzErforderlich),
            auslastungsziel: Number(model.kpis.auslastungsziel) || 0,
            monatsstundenGesamt: Number(model.kpis.monatsstundenGesamt) || 0,
            anlaufAuslastungProMonat: model.months.map((m) => ({
                month: m.month,
                anlaufAuslastung: Number(m.anlaufAuslastung) || 0,
            })),
        },
        liquiditaetUndRentabilitaet: {
            breakEvenMonat: model.breakEvenMonat,
            monate: monateGerundet,
        },
        investitionsZeitraeume,
        aufbauUndVerbesserung: {
            available: aufbauAvailable,
            sourceLabel: aufbau?.sourceLabel || 'Kostenstruktur · Planungsanalyse',
            szenarioName: aufbau?.szenarioName || '—',
            basisDatenTyp: aufbau?.basisDatenTyp ?? null,
            hebel: hebelAnonym,
            summary: aufbauAvailable ? aufbau?.summary ?? null : null,
        },
        promptSections: PROMPT_SECTIONS,
    };
}

export type GeneratedSectionText = PEStartGeneratedSection & {
    id: BusinessPlanSectionId | string;
};

export type PEStartTextEngineProps = {
    className?: string;
    onGenerateText?: (context: BusinessPlanContext) => Promise<GeneratedSectionText[] | string>;
    isGenerating?: boolean;
};

async function copyText(text: string): Promise<boolean> {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* fallback */
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}

export function formatEuroDoc(value: number, currencyCode = 'EUR'): string {
    try {
        return Math.round(Number(value) || 0).toLocaleString('de-DE', {
            style: 'currency',
            currency: currencyCode || 'EUR',
            maximumFractionDigits: 0,
        });
    } catch {
        return `${Math.round(Number(value) || 0).toLocaleString('de-DE')} €`;
    }
}

function DocumentKvRow({
    label,
    value,
    strong,
}: {
    label: string;
    value: string;
    strong?: boolean;
}) {
    return (
        <div className="flex items-baseline justify-between gap-4 border-b border-stone-200 py-1.5 text-[13px] last:border-0">
            <span className="text-stone-600">{label}</span>
            <span
                className={`tabular-nums text-stone-900 ${strong ? 'font-semibold' : 'font-medium'}`}
            >
                {value}
            </span>
        </div>
    );
}

function BusinessPlanA4Document({
    context,
    generatedSections,
}: {
    context: BusinessPlanContext;
    generatedSections: GeneratedSectionText[];
}) {
    const currency = context.meta.currencyCode || 'EUR';
    const pk = context.privatkostenAufschluesselung;
    const bwa = context.bwaErtragslage;

    return (
        <article className="mx-auto w-full max-w-[210mm] bg-white px-8 py-10 text-stone-900 shadow-[0_8px_40px_rgba(0,0,0,0.35)] sm:px-12 print:shadow-none">
            <header className="border-b-2 border-stone-800 pb-5">
                <div className="flex items-end justify-between gap-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                        PreisExpert
                    </p>
                    <p className="text-[10px] text-stone-500">
                        PEStart · Businessplan &amp; Anlaufprognose
                    </p>
                </div>
                <p className="mt-4 text-base font-bold text-stone-900">
                    {context.stammdaten.inhaberName}
                </p>
                <p className="mt-1 text-sm font-medium text-stone-700">
                    {context.stammdaten.firmenname}
                </p>
                <p className="mt-1 text-sm text-stone-500">
                    {context.stammdaten.adresseZeile}
                </p>
                <p className="mt-3 text-xs text-stone-500">
                    {context.stammdaten.datum} · {context.stammdaten.foundingTypeLabel}
                </p>
            </header>

            <section className="mt-8">
                <h3 className="mb-3 border-b border-amber-700/40 pb-1 text-xs font-bold uppercase tracking-wider text-amber-800">
                    Gründerprofil & Vorsorge (PEStart / KfW)
                </h3>
                {(context.executiveSummaryNotes || '').trim() ? (
                    <div className="mb-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                            Vorhabensnotizen
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-[13px] text-stone-700">
                            {context.executiveSummaryNotes}
                        </p>
                    </div>
                ) : null}
                {(context.fachlicheQualifikation || '').trim() ? (
                    <div className="mb-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                            Fachliche Qualifikation
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-[13px] text-stone-700">
                            {context.fachlicheQualifikation}
                        </p>
                    </div>
                ) : null}
                {(context.berufserfahrung || '').trim() ? (
                    <div className="mb-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                            Berufserfahrung
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-[13px] text-stone-700">
                            {context.berufserfahrung}
                        </p>
                    </div>
                ) : null}
                {(context.kaufmaennischeEignung || '').trim() ? (
                    <div className="mb-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                            Kaufmännische Eignung
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-[13px] text-stone-700">
                            {context.kaufmaennischeEignung}
                        </p>
                    </div>
                ) : null}
                <div className="mt-3 space-y-1">
                    <DocumentKvRow
                        label="Private Altersvorsorge (€ / Monat)"
                        value={formatEuroDoc(context.privateAltersvorsorge || 0, currency)}
                    />
                    <DocumentKvRow
                        label="Privater Liquiditätspuffer (€ / Monat)"
                        value={formatEuroDoc(context.liquiditaetspufferMonat || 0, currency)}
                    />
                </div>
            </section>

            <section className="mt-8">
                <h3 className="mb-3 border-b border-amber-700/40 pb-1 text-xs font-bold uppercase tracking-wider text-amber-800">
                    Privatkostenkompass — Unternehmerlohn
                </h3>
                <p className="mb-3 text-[11px] text-stone-500">{pk.hinweis}</p>
                <div className="space-y-1">
                    {pk.kategorien.map((k) => (
                        <DocumentKvRow
                            key={k.id + k.label}
                            label={k.label}
                            value={formatEuroDoc(k.betragMonat, currency)}
                        />
                    ))}
                    <DocumentKvRow
                        label="Summe Unternehmerlohn (Monat)"
                        value={formatEuroDoc(pk.summeMonat, currency)}
                        strong
                    />
                    <DocumentKvRow
                        label="Summe gerundet (Bank)"
                        value={formatEuroDoc(pk.summeGerundet, currency)}
                        strong
                    />
                </div>
            </section>

            <section className="mt-8">
                <h3 className="mb-3 border-b border-amber-700/40 pb-1 text-xs font-bold uppercase tracking-wider text-amber-800">
                    BWA-Ertragslage (Monatsbasis)
                </h3>
                <div className="mb-4 space-y-1">
                    <DocumentKvRow
                        label="Planumsatz netto"
                        value={formatEuroDoc(bwa.basis.planumsatzNetto, currency)}
                    />
                    <DocumentKvRow
                        label={`Wareneinsatz (${bwa.basis.baseMaterialProzent.toFixed(1)} %)`}
                        value={formatEuroDoc(bwa.basis.materialkostenMonat, currency)}
                    />
                    <DocumentKvRow
                        label="Personalkosten brutto"
                        value={formatEuroDoc(bwa.basis.personalkostenBrutto, currency)}
                    />
                    <DocumentKvRow
                        label="Raumkosten"
                        value={formatEuroDoc(bwa.basis.raumkostenMonat, currency)}
                    />
                    <DocumentKvRow
                        label="Sonstige Fixkosten (Betrieb)"
                        value={formatEuroDoc(bwa.basis.sonstigeFixkostenMonat, currency)}
                    />
                    <DocumentKvRow
                        label="Kapitaldienst / Monat"
                        value={formatEuroDoc(bwa.basis.kapitaldienstMonat, currency)}
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] border-collapse text-[11px]">
                        <thead>
                            <tr className="border-b border-stone-300 text-left text-stone-500">
                                <th className="py-1.5 pr-2 font-semibold">Position</th>
                                {bwa.monate.map((m) => (
                                    <th
                                        key={m.month}
                                        className="py-1.5 px-1 text-right font-semibold"
                                    >
                                        M{m.month}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-stone-800">
                            {(
                                [
                                    ['Umsatzerlöse', 'umsatzerloese'],
                                    ['− Material / Ware', 'materialaufwand'],
                                    ['= Rohertrag', 'rohertrag'],
                                    ['− Personalkosten', 'personalkosten'],
                                    ['− Raum & Fixkosten', 'raumUndFixkosten'],
                                    ['= EBITDA', 'operativesErgebnisEbitda'],
                                    ['− Kapitaldienst', 'kapitaldienst'],
                                    ['= Vorsteuer-Erg.', 'vorsteuerErgebnis'],
                                    ['Liquiditätssaldo', 'liquiditaetssaldo'],
                                ] as const
                            ).map(([label, key]) => (
                                <tr key={key} className="border-b border-stone-100">
                                    <td className="py-1 pr-2 text-stone-600">{label}</td>
                                    {bwa.monate.map((m) => (
                                        <td
                                            key={m.month}
                                            className="py-1 px-1 text-right tabular-nums"
                                        >
                                            {formatEuroDoc(m[key], currency)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {generatedSections.length > 0 ? (
                <div className="mt-10 space-y-8">
                    {generatedSections.map((sec, idx) => (
                        <section key={sec.id || `sec-${idx}`}>
                            <h3 className="mb-3 border-b border-amber-700/40 pb-2 text-sm font-bold uppercase tracking-wider text-amber-900">
                                {sec.title}
                            </h3>
                            <div className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-stone-700">
                                {sec.text}
                            </div>
                        </section>
                    ))}
                </div>
            ) : (
                <p className="mt-10 text-sm text-stone-400">
                    Noch keine KI-Kapitel. Briefkopf, Privatkosten und BWA sind bereits
                    live — „Businessplan generieren“ erzeugt die Fließtext-Kapitel.
                </p>
            )}
        </article>
    );
}

const PEStartTextEngine: React.FC<PEStartTextEngineProps> = ({
    className,
    onGenerateText,
    isGenerating: isGeneratingProp,
}) => {
    const calc = useCalculationsSelector() as any;
    const privateCostData = useAppStore((s) => s.clientData?.privateCostData);
    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);
    const appSlice = useAppStore(
        useShallow((s) => ({
            planungsAnalysePrognose: s.planungsAnalysePrognose,
            planungsAnalyseArchiv: s.planungsAnalyseArchiv,
            planungsAnalyseActiveSzenarioId: s.planungsAnalyseActiveSzenarioId,
            planungsAnalyseBasisDatenTyp: s.planungsAnalyseBasisDatenTyp,
            peProfitDataVersion: s.peProfitDataVersion,
            clientData: s.clientData,
        }))
    );

    const peState = usePEStartStore(
        useShallow((s) => ({
            foundingType: s.foundingType,
            subsidies: s.subsidies,
            investments: s.investments,
            staffFlags: s.staffFlags,
            loanPlan: s.loanPlan,
            selectedPlanungsrechnerSzenarioId: s.selectedPlanungsrechnerSzenarioId,
            generatedSections: s.generatedSections,
            privateAltersvorsorge: s.privateAltersvorsorge,
            liquiditaetspufferMonat: s.liquiditaetspufferMonat,
            fachlicheQualifikation: s.fachlicheQualifikation,
            berufserfahrung: s.berufserfahrung,
            kaufmaennischeEignung: s.kaufmaennischeEignung,
            executiveSummaryNotes: s.executiveSummaryNotes,
        }))
    );
    const generatedSections = usePEStartStore((s) => s.generatedSections);
    const setGeneratedSectionsStore = usePEStartStore((s) => s.setGeneratedSections);
    const clearGeneratedSections = usePEStartStore((s) => s.clearGeneratedSections);
    const activeArchivId = usePEStartStore((s) => s.activeArchivId);
    const setClientData = useAppStore((s) => s.setClientData);

    const persistGeneratedToArchiv = useCallback(
        (sections: PEStartGeneratedSection[]) => {
            const normalized = normalizeGeneratedSections(sections);
            setGeneratedSectionsStore(normalized);
            if (!activeArchivId || typeof setClientData !== 'function') return;
            setClientData((prev: any) => {
                const texts = {
                    ...(prev?.peStartGeneratedTexts &&
                    typeof prev.peStartGeneratedTexts === 'object'
                        ? prev.peStartGeneratedTexts
                        : {}),
                    [activeArchivId]: normalized,
                };
                const archivRaw = Array.isArray(prev?.peStartArchiv)
                    ? prev.peStartArchiv
                    : [];
                const peStartArchiv = archivRaw.map((e: any) => {
                    if (!e || e.id !== activeArchivId) return e;
                    return {
                        ...e,
                        state: {
                            ...(e.state && typeof e.state === 'object' ? e.state : {}),
                            generatedSections: normalized,
                        },
                    };
                });
                return {
                    ...prev,
                    peStartGeneratedTexts: texts,
                    peStartArchiv,
                };
            });
        },
        [activeArchivId, setClientData, setGeneratedSectionsStore]
    );

    const aufbau = useMemo(
        () =>
            computePEStartAufbauPlan(
                appSlice,
                calc,
                clientData,
                settings,
                peState.selectedPlanungsrechnerSzenarioId
            ),
        [appSlice, calc, clientData, settings, peState.selectedPlanungsrechnerSzenarioId]
    );

    const context = useMemo(
        () => buildBusinessPlanContext(peState, calc, privateCostData, aufbau, clientData),
        [peState, calc, privateCostData, aufbau, clientData]
    );

    const [localGenerating, setLocalGenerating] = useState(false);
    const [isPdfSaving, setIsPdfSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState<string | null>(null);

    const isGenerating = isGeneratingProp ?? localGenerating;
    const hasBackend = typeof onGenerateText === 'function';

    const clientLabel = useMemo(() => {
        const raw =
            clientData?.firmenname ||
            clientData?.salonname ||
            clientData?.name ||
            '';
        return String(raw).trim();
    }, [clientData?.firmenname, clientData?.salonname, clientData?.name]);

    const clientSubline = useMemo(() => {
        const parts = [
            clientData?.unternehmensform,
            [clientData?.strasse, clientData?.plz, clientData?.ort].filter(Boolean).join(' '),
        ].filter((p) => p && String(p).trim());
        return parts.join(' · ');
    }, [
        clientData?.unternehmensform,
        clientData?.strasse,
        clientData?.plz,
        clientData?.ort,
    ]);

    const handleGenerate = useCallback(async () => {
        if (!hasBackend || !onGenerateText) {
            setStatusMsg('Textgenerierung vorübergehend nicht verfügbar.');
            return;
        }
        setLocalGenerating(true);
        setStatusMsg(null);
        try {
            const result = await onGenerateText(context);
            let next: PEStartGeneratedSection[] = [];
            if (typeof result === 'string') {
                next = [
                    { id: 'executive_summary', title: 'Generierter Text', text: result },
                ];
            } else if (Array.isArray(result)) {
                next = normalizeGeneratedSections(result);
            } else {
                setStatusMsg('Unerwartetes Antwortformat vom Backend.');
                return;
            }
            persistGeneratedToArchiv(next);
            setStatusMsg('Businessplan aktualisiert und gespeichert.');
        } catch (e) {
            setStatusMsg(e instanceof Error ? e.message : 'Generierung fehlgeschlagen.');
        } finally {
            setLocalGenerating(false);
        }
    }, [context, hasBackend, onGenerateText, persistGeneratedToArchiv]);

    const handleDiscardGenerated = useCallback(() => {
        clearGeneratedSections();
        persistGeneratedToArchiv([]);
        setStatusMsg('Generierte Texte verworfen — Neu-Generieren ist möglich.');
    }, [clearGeneratedSections, persistGeneratedToArchiv]);

    const fullDocumentText = useMemo(() => {
        if (generatedSections.length === 0) return '';
        return generatedSections
            .map((s) => `${s.title}\n\n${s.text}`)
            .join('\n\n\n');
    }, [generatedSections]);

    const handleCopyFullDocument = useCallback(async () => {
        if (!fullDocumentText.trim()) {
            setStatusMsg('Kein generierter Text zum Kopieren.');
            return;
        }
        const ok = await copyText(fullDocumentText);
        setStatusMsg(
            ok
                ? 'Gesamter Businessplan in die Zwischenablage kopiert.'
                : 'Kopieren fehlgeschlagen.'
        );
    }, [fullDocumentText]);

    const handleDownloadPdf = useCallback(async () => {
        if (generatedSections.length === 0) {
            setStatusMsg('Bitte zuerst den Businessplan generieren.');
            return;
        }
        setIsPdfSaving(true);
        setStatusMsg(null);
        try {
            downloadPEStartBusinessPlanPdf({
                sections: generatedSections,
                context,
                clientLabel: clientLabel || undefined,
                clientSubline: clientSubline || undefined,
            });
            setStatusMsg('PDF heruntergeladen.');
        } catch (e) {
            setStatusMsg(
                e instanceof Error ? e.message : 'PDF-Export fehlgeschlagen.'
            );
        } finally {
            setIsPdfSaving(false);
        }
    }, [generatedSections, context, clientLabel, clientSubline]);

    return (
        <section
            className={`min-w-0 overflow-x-hidden rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 text-base ${className ?? ''}`}
        >
            <header className="mb-6 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · Text-Engine
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    KI-Businessplan-Kontext
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Prompt-Context aus Live-Totpunkt, Privatkostenkompass, Team-Kalkulation und
                    Gründungs-Extras.
                </p>
            </header>

            <div className="mb-6">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Generierbare Bausteine
                </p>
                <ul className="space-y-2">
                    {context.promptSections.map((sec) => (
                        <li
                            key={sec.id}
                            className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3"
                        >
                            <p className="text-sm font-bold text-pe-accent-text dark:text-[#E6C299]">{sec.title}</p>
                            <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">{sec.instruction}</p>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">Typ</p>
                    <p className="mt-1 text-sm font-semibold text-pe-text dark:text-white">
                        {context.meta.foundingTypeLabel}
                    </p>
                </div>
                <div className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">Totpunkt</p>
                    <p className="mt-1 text-sm font-semibold tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                        {context.managementSummary.totpunktMonatBrutto.toLocaleString('de-DE', {
                            style: 'currency',
                            currency: context.meta.currencyCode,
                            maximumFractionDigits: 0,
                        })}
                    </p>
                </div>
                <div className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        {context.managementSummary.unternehmerlohnSource === 'kompass'
                            ? 'Kompass-UL'
                            : 'Pauschal-UL'}
                    </p>
                    <p className="mt-1 text-sm font-semibold tabular-nums text-pe-text dark:text-white">
                        {context.managementSummary.unternehmerlohnKompass.toLocaleString('de-DE', {
                            style: 'currency',
                            currency: context.meta.currencyCode,
                            maximumFractionDigits: 0,
                        })}
                    </p>
                </div>
                <div className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">Break-Even</p>
                    <p className="mt-1 text-sm font-semibold text-pe-text dark:text-white">
                        {context.managementSummary.breakEvenMonat
                            ? `Monat ${context.managementSummary.breakEvenMonat}`
                            : 'offen'}
                    </p>
                </div>
            </div>

            <div className="mb-6 flex flex-wrap gap-2 border-b border-pe-border dark:border-[#222] pb-6">
                <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={isGenerating || !hasBackend}
                    className={`inline-flex items-center rounded-lg border px-3 py-2.5 text-sm font-bold uppercase tracking-wider ${
                        hasBackend
                            ? 'border-[#E6C299]/40 bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25 disabled:opacity-40'
                            : 'border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] text-stone-500 dark:text-slate-500'
                    }`}
                >
                    {isGenerating ? 'Generiert…' : 'Businessplan generieren'}
                </button>
                <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={isPdfSaving || generatedSections.length === 0}
                    className="inline-flex items-center rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    {isPdfSaving ? 'PDF wird erstellt…' : 'Businessplan als PDF herunterladen'}
                </button>
                <button
                    type="button"
                    onClick={handleDiscardGenerated}
                    disabled={generatedSections.length === 0 || isGenerating}
                    className="inline-flex items-center rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-rose-300/90 hover:border-rose-400/40 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    Generierte Texte verwerfen
                </button>
            </div>

            {statusMsg && (
                <p className="mb-4 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-2.5 text-sm text-pe-muted dark:text-slate-400">
                    {statusMsg}
                </p>
            )}

            <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                        Businessplan-Dokument
                    </p>
                    <button
                        type="button"
                        onClick={handleCopyFullDocument}
                        disabled={generatedSections.length === 0}
                        className="rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2 text-sm font-medium text-pe-accent-text dark:text-[#E6C299] hover:border-[#E6C299]/40 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Gesamten Businessplan kopieren
                    </button>
                </div>

                <div className="overflow-x-auto rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#1a1a1a] p-3 sm:p-5">
                    <BusinessPlanA4Document
                        context={context}
                        generatedSections={generatedSections}
                    />
                </div>
            </div>
        </section>
    );
};

export default PEStartTextEngine;
